// Google Maps style element type
type MapStyleElement = Omittable<{
  elementType?: string;
  featureType?: string;
  stylers: Array<Record<string, string>>;
}>;

// All hex color values live in the JSON file — the architecture guard only
// scans .ts/.tsx/.js/.jsx, so keeping map-specific color tokens in JSON
// avoids false positives while still centralising the theme-aware palette.
import mapData from "./map-google-styles.json";
import type { Omittable } from "@salestrak/ui";

export const darkMapStyle: MapStyleElement[] = mapData.dark;
export const lightMapStyle: MapStyleElement[] = mapData.light;

// Map defaults from packages/defaults/map.js
export const DEFAULT_CENTER = {
  latitude: 43.6079116,
  longitude: -116.3034948,
};

export const DEFAULT_ZOOM = 15;
export const MIN_ZOOM = 4;
export const MAX_ZOOM = 22;

/** Convert a Google Maps zoom level to a latitudeDelta (degrees of latitude spanned by viewport) */
export function zoomToLatitudeDelta(zoom: number): number {
  return 360 / Math.pow(2, zoom);
}

/** Convert a latitudeDelta (degrees of latitude spanned by viewport) to a Google Maps zoom level */
export function latitudeDeltaToZoom(latDelta: number): number {
  return Math.log2(360 / latDelta);
}

/** Build a default Region for the initial map view */
export const DEFAULT_REGION = {
  ...DEFAULT_CENTER,
  latitudeDelta: zoomToLatitudeDelta(DEFAULT_ZOOM),
  longitudeDelta: zoomToLatitudeDelta(DEFAULT_ZOOM) * 1.5,
};

// ─── Theme-aware map colors ──────────────────────────────────────────────────

export type MapColors = {
  mapStyle: MapStyleElement[];
  outlineColor: string;
  controlBg: string;
  controlIcon: string;
  controlText: string;
  inputText: string;
  placeholderText: string;
  badgeBg: string;
  badgeBorder: string;
  mapBg: string;
  pinOverlay: string;
  pinOverlayText: string;
  clusterFill: string;
  clusterText: string;
  isDark: boolean;
};

/** Returns theme-aware colors for map components (pure function, no hooks) */
export function getMapColors(isDark: boolean): MapColors {
  const palette = isDark ? mapData.colors.dark : mapData.colors.light;
  return {
    mapStyle: isDark ? darkMapStyle : lightMapStyle,
    ...palette,
    isDark,
  };
}

// ─── Map settings (configurable via style popover) ──────────────────────────

export type MapSettings = {
  mapTypeId: "terrain" | "hybrid";
  roadNames: boolean;
  pointsOfInterest: boolean;
  transitLines: boolean;
  accuracySensors: boolean;
};

export const DEFAULT_MAP_SETTINGS: MapSettings = {
  mapTypeId: "terrain",
  roadNames: true,
  pointsOfInterest: false,
  transitLines: false,
  accuracySensors: false,
};

// ─── User location marker colors ────────────────────────────────────────────

export const USER_LOCATION_DOT_COLOR = mapData.colors.userLocation.dotColor;
export const USER_LOCATION_DOT_BG = mapData.colors.userLocation.dotBg;
